import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { environment } from '../../../../environments/environment';
import { FormRenderComponent } from '../../widgets/form-render/form-render.component';
import { FormsServiceService } from '../../../api/services/forms-service.service';
import { WorkflowService } from '../../../api/services/workflow.service';
import { SseService } from '../../../api/services/sse.service';
import { CommonModule } from '@angular/common';
import { ModalRendererComponent } from '../../widgets/modal-renderer/modal-renderer.component';
import { FORM_ID } from '../../../helpers/form-id.enum';

import { Subscription } from 'rxjs';
import { IntegrationStateService } from '../../../features/admin/security-integration-hub/integration-marketplace/integration-state.service';
import { IntegrationIconService } from '../../../api/services/integration-icon.service';

@Component({
  selector: 'app-integration-cards-pro',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ModalRendererComponent,
  ],
  templateUrl: './integration-cards-pro.component.html',
  styleUrl: './integration-cards-pro.component.css',
})
export class IntegrationCardsProComponent {
  @Input() formGroup!: FormGroup;
  @Input() fields: any;
  @Output() loadingChange = new EventEmitter<boolean>();
  @Input() selectedCategory: any;
  @Input() selectedIntegration: string | null = null;
  @Input() limit: number = 500;
  searchQuery: string = '';
  private integrationSub?: Subscription;
  private searchSub?: Subscription;
  private categorySub?: Subscription;
  private workflowSub?: Subscription;
  private sseSub?: Subscription;
  showValidationError = false;
  fieldsData: any;
  endpoint = environment.api_endpoint;
  selectedOptions: any[] = [];
  readonly defaultIconUrl = '/assets/media/integration/default.svg';
  loadingCards: Set<string> = new Set();
  modalTitle: string = '';
  activeModalContext: any;
  activeComingSoonOption: any;
  workflowData: any;
  currentRecordId: string | undefined;
  currentNodeId: any;
  currentNode: any;
  recordID: any;
  nextNodes: any;
  targetWorkflowId: any;
  targetValue: any;
  workflowId: any;
  accessToken: any;
  previousNodeId: any;
  intervalId: any;
  isLoading: boolean = false;
  @ViewChild(FormRenderComponent) FormRenderComponent!: FormRenderComponent;
  @ViewChild(ModalRendererComponent) modalRendererRef!: ModalRendererComponent;

  constructor(
    private formService: FormsServiceService,
    private workflowService: WorkflowService,
    private SseService: SseService,
    private cdr: ChangeDetectorRef,
    private integrationStateService: IntegrationStateService,
    private integrationIconService: IntegrationIconService,
  ) {}

  ngOnInit() {
    this.getData();
    this.integrationSub =
      this.integrationStateService.selectedIntegration$.subscribe(
        (integration: string | null) => {
          this.selectedIntegration = integration;
          this.cdr.detectChanges();
        },
      );
    this.searchSub = this.integrationStateService.searchQuery$.subscribe(
      (query: string) => {
        this.searchQuery = query;
        this.cdr.detectChanges();
      },
    );
    this.categorySub = this.integrationStateService.selectedCategory$.subscribe(
      (category: string | null) => {
        this.selectedCategory = category;
        this.cdr.detectChanges();
      },
    );
  }

  ngOnDestroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    this.integrationSub?.unsubscribe();
    this.searchSub?.unsubscribe();
    this.categorySub?.unsubscribe();
    this.workflowSub?.unsubscribe();
    this.sseSub?.unsubscribe();
  }

  shouldShowOption(option: any): boolean {
    if (!option || option._id === 'None') {
      return false;
    }
    if (this.selectedCategory) {
      const selectedCat = this.selectedCategory.toLowerCase().trim();
      const optionCat = (option.category || option.categoryName || '')
        .toLowerCase()
        .trim();

      const isIamMatch =
        (selectedCat === 'iam' ||
          selectedCat === 'identity management' ||
          selectedCat === 'identity & access management') &&
        (optionCat === 'iam' ||
          optionCat === 'identity management' ||
          optionCat === 'identity & access management');

      if (optionCat !== selectedCat && !isIamMatch) {
        return false;
      }
    }
    if (this.selectedIntegration) {
      const filterVal = this.selectedIntegration.toLowerCase().trim();
      const optionId = option._id?.toString().toLowerCase();
      const platformName = option.platformName?.toLowerCase().trim();
      const name = option.name?.toLowerCase().trim();

      const isMatch =
        optionId === filterVal ||
        platformName === filterVal ||
        name === filterVal;

      if (!isMatch) {
        return false;
      }
    }
    if (this.searchQuery && this.searchQuery.trim() !== '') {
      const q = this.searchQuery.toLowerCase().trim();
      const platformName = option.platformName?.toLowerCase() || '';
      const description = option.description?.toLowerCase() || '';
      const match = platformName.includes(q) || description.includes(q);
      if (!match) {
        return false;
      }
    }
    return true;
  }

  hasVisibleOptions(): boolean {
    if (!this.fieldsData || this.fieldsData.length === 0) {
      return false;
    }
    return this.fieldsData.some((option: any) => this.shouldShowOption(option));
  }

  getCategoryDisplayName(): string {
    if (this.selectedCategory) {
      return this.selectedCategory;
    }
    if (
      this.searchQuery &&
      this.searchQuery.trim() !== '' &&
      this.fieldsData &&
      this.fieldsData.length > 0
    ) {
      const firstOpt = this.fieldsData[0];
      return (
        firstOpt.category || firstOpt.categoryName || this.fields?.displayName
      );
    }
    return this.fields?.displayName;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      (changes['formGroup'] || changes['fields'] || changes['limit']) &&
      this.formGroup &&
      this.fields
    ) {
      this.getData();
    }
  }

  get control() {
    return this.formGroup.get(this.fields.fieldName);
  }

  sortFieldsData() {
    if (!this.fieldsData || !Array.isArray(this.fieldsData)) return;
    this.fieldsData.sort((a: any, b: any) => {
      const aConn = this.isSelected(a) ? 1 : 0;
      const bConn = this.isSelected(b) ? 1 : 0;
      if (aConn !== bConn) {
        return bConn - aConn;
      }
      const aHasDeps = a.fieldDependencies != null ? 1 : 0;
      const bHasDeps = b.fieldDependencies != null ? 1 : 0;
      if (aHasDeps !== bHasDeps) {
        return bHasDeps - aHasDeps;
      }
      const nameA = (a.platformName || a.name || '').toLowerCase();
      const nameB = (b.platformName || b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }

  getData() {
    this.isLoading = true;
    this.loadingChange.emit(true);
    if (this.fields && this.fields.options.isStatic == true) {
      this.fieldsData = this.fields?.options?.staticOptions;
      this.sortFieldsData();
      if (this.fields?.value?.length && this.fieldsData) {
        const selectedIds = this.fields.value.map((v: any) => v.toString());
        const validSelections = this.fieldsData
          .map((item: any) => item._id.toString())
          .filter((id: string) => selectedIds.includes(id));
        this.control?.setValue(validSelections, { emitEvent: false });
      }
      this.restoreTagsFromSelectedOptions();
      this.isLoading = false;
      this.loadingChange.emit(false);
    } else if (this.fields && this.fields.options.isStatic == false) {
      this.isLoading = true;
      const formId = this.fields.options.source.formID;
      const api_endpoint = this.endpoint;
      const method = this.fields.options.source.method;
      let url = this.fields.options.source.apiUrl;
      const replacements = { formID: formId, api_endpoint: api_endpoint };
      url = this.formService.replaceTemplateTokens(url, replacements);

      const categoryName =
        typeof this.selectedCategory === 'string'
          ? this.selectedCategory
          : this.selectedCategory?.displayName ||
            this.selectedCategory?.categoryName ||
            this.selectedCategory?.categoryCode ||
            '';
      if (categoryName && !url.includes('category=')) {
        const separator = url.includes('?') ? '&' : '?';
        url += `${separator}category=${encodeURIComponent(categoryName)}`;
      }
      const limitVal = this.limit ?? 500;
      if (!url.includes('limit=')) {
        const separator = url.includes('?') ? '&' : '?';
        url += `${separator}limit=${limitVal}`;
      } else {
        url = url.replace(/([?&])limit=\d+/, `$1limit=${limitVal}`);
      }
      if (!url.includes('sort=')) {
        const separator = url.includes('?') ? '&' : '?';
        url += `${separator}sort=-platformName`;
      }

      const accessToken = localStorage.getItem('accessToken');
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      };
      this.formService.loadData(method, url, headers).subscribe({
        next: (res) => {
          this.fieldsData = res?.data?.records || [];
          this.sortFieldsData();
          this.restoreTagsFromSelectedOptions();
          this.isLoading = false;
          this.loadingChange.emit(false);
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.isLoading = false;
          this.loadingChange.emit(false);
          this.cdr.detectChanges();
        },
      });
    }
  }

  restoreTagsFromSelectedOptions() {
    if (!this.fieldsData?.length) return;
    const currentValue = (this.fields?.value || []).map((v: any) =>
      v.toString(),
    );
    const selectedOptions = this.fieldsData.filter((opt: any) =>
      currentValue.includes(opt._id?.toString()),
    );
    const restoredIds = selectedOptions.map((opt: any) => opt._id.toString());
    if (JSON.stringify(currentValue) !== JSON.stringify(restoredIds)) {
      this.control?.setValue(restoredIds, { emitEvent: false });
    }
    const selectedNames = selectedOptions.map((opt: any) => opt.name);
    this.formGroup
      .get(this.fields.fieldName + 'Tags')
      ?.setValue(selectedNames, { emitEvent: false });
  }

  isSelected(option: any): boolean {
    if (!option || !option.status || option.fieldDependencies == null)
      return false;
    const status = option.status.toLowerCase();
    return (
      status === 'connected' ||
      status === 'reconnect' ||
      status === 'reconnected' ||
      status === 'failed' ||
      status === 'connection_failed' ||
      status === 'connection failed'
    );
  }

  toggleSelection(option: any, type: any): void {
    this.modalTitle = '';
    const optionId = option._id?.toString();
    const optionName = option.platformName;
    const tagsControl = this.formGroup.get(this.fields.fieldName + 'Tags');
    const isAlreadySelected = this.isSelected(option);

    if (this.fields?.fieldType === 'singleSelect') {
      if (isAlreadySelected && type === 'disconnect') {
        const fieldDeps = option?.fieldDependencies || [];
        const onClickDep =
          fieldDeps.find((dep: any) => dep.trigger === 'onClick') ||
          fieldDeps[0];
        const disconnectBlock = onClickDep?.condition?.find(
          (block: any) => block.action === 'disconnect',
        );
        const modalCond = disconnectBlock?.conditions?.find(
          (c: any) =>
            c.type === 'modal' && c.targetType === 'form' && c.targetValue,
        );
        if (modalCond) {
          this.activeModalContext = {
            workflowId: null,
            nodeId: modalCond.targetValue,
            selectedOption: option,
            selectedName: option.platformName,
            fieldName: this.fields.fieldName,
            fieldID: optionId,
            categoryCode:
              option.categoryCode ||
              option.integration?.categoryCode ||
              this.selectedCategory?.categoryCode ||
              this.selectedCategory ||
              this.fields?.displayName ||
              '',
          };
          this.loadNode(modalCond.targetValue);
          const modalId = `connection_modal_${this.fields.fieldName}`;
          document.getElementById(modalId)?.classList.add('show');
          this.loadingCards.delete(optionId);
          return;
        }
        this.control?.setValue([]);
        tagsControl?.setValue([]);
        this.selectedOptions = [];
        this.loadingCards.delete(optionId);
        return;
      }

      if (type === 'connect') {
        if (option.fieldDependencies == null) {
          this.activeComingSoonOption = option;
          const modalId = `coming_soon_modal_${this.fields.fieldName}`;
          const mEl = document.getElementById(modalId);
          if (mEl) mEl.classList.add('show');
          return;
        }
        this.loadingCards.add(optionId);
        const statusLower = option.status?.toLowerCase();
        if (
          statusLower === 'reconnect' ||
          statusLower === 'failed' ||
          statusLower === 'connection_failed' ||
          statusLower === 'connection failed'
        ) {
          const fieldDeps = option?.fieldDependencies || [];
          const onClickDep =
            fieldDeps.find((dep: any) => dep.trigger === 'onClick') ||
            fieldDeps[0];
          const connectBlock = onClickDep?.condition?.find(
            (block: any) => block.action === 'connect',
          );
          const workflowCond = connectBlock?.conditions?.find(
            (c: any) => c.targetType === 'workflow' && c.targetValue,
          );
          if (workflowCond) {
            const workflowId = workflowCond.targetValue;
            this.workflowService.resetWorkflowInstance(workflowId).subscribe({
              next: (res) => {
                this.activeModalContext = {
                  workflowId: workflowId,
                  formId: null,
                  selectedOption: option,
                  authUrl: option.integration?.authUrl,
                  selectedName: option.platformName,
                  fieldName: this.fields.fieldName,
                  fieldID: optionId,
                  categoryCode:
                    option.categoryCode ||
                    option.integration?.categoryCode ||
                    this.selectedCategory?.categoryCode ||
                    this.selectedCategory ||
                    this.fields?.displayName ||
                    '',
                };
                this.loadWorkflow(workflowId, 0);
                const modalId = `connection_modal_${this.fields.fieldName}`;
                const mEl = document.getElementById(modalId);
                if (mEl) mEl.classList.add('show');
                this.loadingCards.delete(optionId);
                this.cdr.detectChanges();
              },
              error: (err) => {
                this.loadingCards.delete(optionId);
                this.cdr.detectChanges();
              },
            });
            return;
          }
        }

        const fieldDeps = option?.fieldDependencies || [];
        const onClickDep =
          fieldDeps.find((dep: any) => dep.trigger === 'onClick') ||
          fieldDeps[0];
        const connectBlock = onClickDep?.condition?.find(
          (block: any) => block.action === 'connect',
        );
        const modalCond = connectBlock?.conditions?.find(
          (c: any) =>
            c.type === 'modal' &&
            ['workflow', 'form'].includes(c.targetType) &&
            c.targetValue,
        );
        if (modalCond) {
          this.activeModalContext = {
            workflowId:
              modalCond.targetType === 'workflow'
                ? modalCond.targetValue
                : null,
            formId:
              modalCond.targetType === 'form' ? modalCond.targetValue : null,
            selectedOption: option,
            authUrl: option.integration?.authUrl,
            selectedName: option.platformName,
            fieldName: this.fields.fieldName,
            fieldID: optionId,
            categoryCode:
              option.categoryCode ||
              option.integration?.categoryCode ||
              this.selectedCategory?.categoryCode ||
              this.selectedCategory ||
              this.fields?.displayName ||
              '',
          };
          if (modalCond.targetType === 'workflow')
            this.loadWorkflow(modalCond.targetValue, 0);
          if (modalCond.targetType === 'form')
            this.loadNode(modalCond.targetValue);
          const modalId = `connection_modal_${this.fields.fieldName}`;
          document.getElementById(modalId)?.classList.add('show');
          this.loadingCards.delete(optionId);
          return;
        }

        if (option.integration?.authUrl) {
          const catCode =
            option.categoryCode ||
            option.integration?.categoryCode ||
            this.selectedCategory?.categoryCode ||
            this.selectedCategory ||
            this.fields?.displayName ||
            '';
          const recordIdVal = String(
            this.recordID ||
              this.activeModalContext?.recordID ||
              this.currentRecordId ||
              '',
          );
          let authUrl = option.integration.authUrl
            .replace('{{base}}', this.endpoint)
            .replace(/\{\{categoryCode\}\}/g, catCode)
            .replace(/\{\{integrationProfileRecordID\}\}/g, recordIdVal);

          this.accessToken = localStorage.getItem('accessToken') || '';
          const separator = authUrl.includes('?') ? '&' : '?';
          authUrl += `${separator}at=${encodeURIComponent(this.accessToken)}`;
          window.open(authUrl, '_blank');
        } else if (
          option.integration?.allowIntegration &&
          option.integration?.url
        ) {
          window.open(option.integration.url, '_blank');
        }

        if (this.sseSub) {
          this.sseSub.unsubscribe();
        }
        this.sseSub = this.SseService.getMessages().subscribe((msg: any) => {
          try {
            const parsed = typeof msg === 'string' ? JSON.parse(msg) : msg;
            if (
              parsed?.type === 'connectionSuccess' ||
              parsed?.type === 'connectSuccess' ||
              parsed?.payload?.status === 200
            ) {
              this.control?.setValue([optionId]);
              tagsControl?.setValue([optionName]);
              this.selectedOptions = [optionId];
              this.control?.markAsTouched();
              this.control?.updateValueAndValidity();
              this.showValidationError = false;
              this.loadingCards.delete(optionId);
              this.sseSub?.unsubscribe();
            }
          } catch (err) {}
          this.cdr.detectChanges();
        });
      }
    }
  }

  loadWorkflow(wId: string, nodeId?: any, nodeIndex?: any): void {
    this.workflowData = null;
    if (this.workflowSub) {
      this.workflowSub.unsubscribe();
    }
    this.workflowSub = this.workflowService.loadWorkflow(wId).subscribe({
      next: ({ workflowData, recordId }) => {
        this.workflowData = workflowData;
        this.currentRecordId = recordId;
        this.workflowId = wId;
        this.cdr.markForCheck();
        if (nodeIndex === 'secondLastNode') {
          const nodes = workflowData?.nodes || [];
          if (nodes.length >= 2) {
            const secondLastNode = nodes[nodes.length - 2];
            this.currentNodeId = secondLastNode._id;
            this.loadNode(secondLastNode._id, recordId);
            return;
          }
        }
        const startNodeId = workflowData?.startNodeId;
        if (startNodeId) {
          this.currentNodeId = startNodeId;
          this.loadNode(startNodeId, recordId);
        }
        this.cdr.detectChanges();
      },
      error: (error: any) => {
        this.cdr.detectChanges();
      },
    });
  }

  loadNode(nodeId: any, recordId?: string): void {
    if (this.activeModalContext?.nodeId === nodeId) {
      this.currentNodeId = nodeId;
      this.currentNode = null;
      this.nextNodes = null;
      this.targetWorkflowId = null;
      return;
    }
    const node = this.workflowData?.nodes?.find((n: any) => n._id === nodeId);
    if (!node) return;
    this.currentNode = node;
    this.currentNodeId = node._id;
    const platformName =
      this.activeModalContext?.selectedOption?.platformName ||
      this.activeModalContext?.selectedName ||
      '';
    this.recordID =
      node.recordId && node.recordId.trim() !== ''
        ? node.recordId
        : recordId || platformName;
    this.loadPreviousNode();
    const nextNodeIndex = this.workflowData?.nodes?.findIndex(
      (n: any) => n._id === this.currentNode?._id,
    );
    const lastNodeIndex = this.workflowData?.nodes?.length - 1;
    const isLastNode = nextNodeIndex === lastNodeIndex;
    if (this.activeModalContext) this.activeModalContext.lastNode = isLastNode;
    const nextButtonObj =
      node.next?.find((n: any) =>
        ['next_button', 'submit_continue'].includes(n.buttonId),
      ) || node.next?.[0];
    if (!nextButtonObj) return;
    const condition = nextButtonObj.condition;
    if (Array.isArray(condition)) {
      for (const cond of condition) {
        if (cond?.targetType === 'node' && cond?.targetValue) {
          this.nextNodes = cond.targetValue;
          return;
        }
        if (cond?.targetNodeId) {
          this.nextNodes = cond.targetNodeId;
          return;
        }
        if (cond?.targetType === 'url' && cond?.targetValue) {
          this.targetValue = cond.targetValue;
          return;
        }
        if (cond?.targetType === 'workflow' && cond?.targetValue) {
          this.targetWorkflowId = cond.targetValue;
          return;
        }
      }
    } else if (typeof condition === 'object' && condition !== null) {
      if (nextButtonObj.targetType === 'node' && nextButtonObj.targetValue)
        this.nextNodes = nextButtonObj.targetValue;
      else if (nextButtonObj.targetNodeId)
        this.nextNodes = nextButtonObj.targetNodeId;
      else if (nextButtonObj.targetType === 'url' && nextButtonObj.targetValue)
        this.targetValue = nextButtonObj.targetValue;
      else if (
        nextButtonObj.targetType === 'workflow' &&
        nextButtonObj.targetValue
      )
        this.targetWorkflowId = nextButtonObj.targetValue;
    }
  }

  validateSelection(): boolean {
    const currentValue = (this.control?.value || []).map((v: any) =>
      v.toString(),
    );
    if (currentValue.length > 0) {
      this.showValidationError = false;
      return true;
    }
    if (this.fields.isRequired) {
      this.showValidationError = true;
      return false;
    }
    this.showValidationError = false;
    return true;
  }

  openConnect(option: any, id: string) {
    const fieldDeps = option?.fieldDependencies || [];
    const onClickDep =
      fieldDeps.find((dep: any) => dep.trigger === 'onClick') || fieldDeps[0];
    if (onClickDep && Array.isArray(onClickDep.condition)) {
      for (const condBlock of onClickDep.condition) {
        const modalCond = condBlock.conditions?.find(
          (c: any) =>
            c.type === 'modal' && c.targetType === 'workflow' && c.targetValue,
        );
        if (modalCond) {
          this.activeModalContext = {
            workflowId: modalCond.targetValue,
            selectedOption: option,
            selectedName: option.platformName,
            recordID: this.recordID,
            workflowData: this.workflowData,
            authUrl: option.integration?.authUrl || null,
            categoryCode:
              option.categoryCode ||
              option.integration?.categoryCode ||
              this.selectedCategory?.categoryCode ||
              this.selectedCategory ||
              this.fields?.displayName ||
              '',
          };
          this.loadWorkflow(
            this.activeModalContext.workflowId,
            0,
            'secondLastNode',
          );
          const modalId = `connection_modal_${this.fields.fieldName}`;
          document.getElementById(modalId)?.classList.add('show');
          return;
        }
      }
    }
  }

  closeOnBackdropClick(event: MouseEvent, modalId: string) {
    const modal = document.getElementById(modalId);
    if (event.target === modal) this.closeModal(modalId);
  }

  closeModal(modalId: string) {
    const modal = document.getElementById(modalId);
    if (modal) modal.classList.remove('show');
    this.activeModalContext = null;
    this.stopPollingFromModalRenderer();

    if (this.workflowSub) {
      this.workflowSub.unsubscribe();
    }
    if (this.sseSub) {
      this.sseSub.unsubscribe();
    }

    // Clear connection/workflow state to avoid stale data next time a card is opened
    this.modalTitle = '';
    this.activeComingSoonOption = null;
    this.workflowData = null;
    this.currentNodeId = null;
    this.currentNode = null;
    this.currentRecordId = undefined;
    this.recordID = null;
    this.nextNodes = null;
    this.targetWorkflowId = null;
    this.targetValue = null;
    this.workflowId = null;
    this.previousNodeId = null;
  }

  stopPollingFromModalRenderer() {
    if (this.modalRendererRef) this.modalRendererRef.stopChildPolling();
  }

  onModalFormSaved(event: any): void {
    const optionId = this.activeModalContext?.selectedOption?._id;
    const tagsControl = this.formGroup.get(this.fields.fieldName + 'Tags');
    if (event?.success) {
      if (event.recordId) {
        this.recordID = event.recordId;
        if (this.activeModalContext) {
          this.activeModalContext.recordID = event.recordId;
        }
      }
      const option = this.activeModalContext?.selectedOption;
      const { nextNodeId, targetValue, targetWorkflowId, isSseTrigger } =
        this.resolveNavigationTargets(this.currentNode);
      const currentIndex = this.workflowData?.nodes?.findIndex(
        (n: any) => n._id === this.currentNode?._id,
      );
      const nextNodeIndex = this.workflowData?.nodes?.findIndex(
        (n: any) => n._id == nextNodeId,
      );
      const lastNodeIndex = this.workflowData?.nodes?.length - 1;
      const isLastNode = currentIndex === lastNodeIndex;
      const isNextNodeTheLastNode = nextNodeIndex === lastNodeIndex;
      const firstNodeId = this.workflowData?.nodes?.[0]?._id;
      if (
        this.currentNode?._id === firstNodeId &&
        option?.integration?.authUrl &&
        this.activeModalContext.selectedName != 'Jira'
      ) {
        const catCode =
          option.categoryCode ||
          option.integration?.categoryCode ||
          this.selectedCategory?.categoryCode ||
          this.selectedCategory ||
          this.fields?.displayName ||
          '';
        const recordIdVal = String(
          this.recordID ||
            this.activeModalContext?.recordID ||
            this.currentRecordId ||
            event.recordId ||
            '',
        );

        let authUrl = option.integration.authUrl
          .replace('{{base}}', this.endpoint)
          .replace(/\{\{categoryCode\}\}/g, catCode)
          .replace(/\{\{integrationProfileRecordID\}\}/g, recordIdVal);

        this.accessToken = localStorage.getItem('accessToken') || '';
        const separator = authUrl.includes('?') ? '&' : '?';
        authUrl += `${separator}at=${encodeURIComponent(this.accessToken)}`;

        window.open(authUrl, '_blank');
      }
      if (isNextNodeTheLastNode && !isLastNode) {
        const newValue = [this.activeModalContext?.selectedOption?._id];
        const newTags = [this.activeModalContext?.selectedOption?.name];
        this.control?.setValue(newValue);
        tagsControl?.setValue(newTags);
        this.currentNode.selectedOptions = newValue;
        this.workflowData.selectedOptions = newValue;
        this.control?.markAsTouched();
        this.control?.updateValueAndValidity();
        this.loadingCards.delete(optionId);
      }
      if (isLastNode) {
        const newValue = [this.activeModalContext?.selectedOption?._id];
        const newTags = [this.activeModalContext?.selectedOption?.name];
        this.control?.setValue(newValue);
        tagsControl?.setValue(newTags);
        this.selectedOptions = [...newValue];
        this.control?.markAsTouched();
        this.control?.updateValueAndValidity();
        this.loadingCards.delete(optionId);
        this.currentNode.selectedOptions = this.selectedOptions;
        this.workflowData.selectedOptions = this.selectedOptions;
        this.selectedOptions = [...newValue];

        const optionCategory =
          option?.category ||
          option?.categoryName ||
          this.activeModalContext?.categoryCode ||
          (typeof this.selectedCategory === 'string'
            ? this.selectedCategory
            : this.selectedCategory?.displayName ||
              this.selectedCategory?.categoryName ||
              '');

        if (optionCategory) {
          this.integrationStateService.setSelectedCategory(optionCategory);
        }

        this.getData();
        this.integrationStateService.triggerRefresh();
      }
      if (nextNodeId && !isSseTrigger) {
        this.loadNode(nextNodeId, event.recordId);
        return;
      }
      if (targetValue) {
        const token = localStorage.getItem('accessToken');
        if (token) {
          window.location.href = `${targetValue}/${token}`;
          return;
        }
      }
      if (targetWorkflowId) {
        this.loadWorkflow(targetWorkflowId);
        return;
      }
      if (isSseTrigger) {
        const sseTrigger = this.currentNode?.triggers?.find(
          (trigger: any) => trigger.event === 'listenSSE',
        );
        if (sseTrigger?.config?.url && sseTrigger?.config?.nextNodeId) {
          const apiEndpoint = environment.api_endpoint;
          const authToken = localStorage.getItem('accessToken') || '';
          const resolvedUrl = sseTrigger.config.url
            .replace('{{api_endpoint}}', apiEndpoint)
            .replace('{{authToken}}', authToken);
          this.loadNode(sseTrigger.config.nextNodeId, event.recordId);
          return;
        }
      }
      const modalId = `connection_modal_${this.fields.fieldName}`;
      const modal = document.getElementById(modalId);
      if (modal) modal.classList.remove('show');
      this.activeModalContext = null;
    }
  }

  resolveNavigationTargets(node: any): {
    nextNodeId: string | null;
    targetValue: string | null;
    targetWorkflowId: string | null;
    recordId: string | null;
    isSseTrigger?: boolean;
  } {
    const fallback = {
      nextNodeId: null,
      targetValue: null,
      targetWorkflowId: null,
      recordId: node?.recordId ?? null,
      isSseTrigger: false,
    };
    if (!node?.next || !Array.isArray(node.next)) return fallback;
    const nextButtonObj =
      node.next.find((n: any) =>
        ['next_button', 'submit_continue'].includes(n.buttonId),
      ) || node.next[0];
    if (!nextButtonObj) return fallback;
    const resolveSseTrigger = (): {
      nextNodeId: string | null;
      isSseTrigger: boolean;
    } => {
      const sseTrigger = node.triggers?.find(
        (trigger: any) => trigger.event === 'listenSSE',
      );
      if (sseTrigger?.config?.nextNodeId)
        return { nextNodeId: sseTrigger.config.nextNodeId, isSseTrigger: true };
      return { nextNodeId: null, isSseTrigger: false };
    };
    const condition = nextButtonObj.condition;
    if (Array.isArray(condition)) {
      for (const cond of condition) {
        if (cond?.targetType === 'node' && cond?.targetValue)
          return { ...fallback, nextNodeId: cond.targetValue };
        if (cond?.targetNodeId)
          return { ...fallback, nextNodeId: cond.targetNodeId };
        if (cond?.targetType === 'url' && cond?.targetValue)
          return { ...fallback, targetValue: cond.targetValue };
        if (cond?.targetType === 'workflow' && cond?.targetValue)
          return { ...fallback, targetWorkflowId: cond.targetValue };
        if (cond?.targetType === 'manual') {
          const { nextNodeId, isSseTrigger } = resolveSseTrigger();
          if (nextNodeId) return { ...fallback, nextNodeId, isSseTrigger };
        }
      }
    } else if (typeof condition === 'object' && condition !== null) {
      if (nextButtonObj.targetType === 'node' && nextButtonObj.targetValue)
        return { ...fallback, nextNodeId: nextButtonObj.targetValue };
      if (nextButtonObj.targetNodeId)
        return { ...fallback, nextNodeId: nextButtonObj.targetNodeId };
      if (nextButtonObj.targetType === 'url' && nextButtonObj.targetValue)
        return { ...fallback, targetValue: nextButtonObj.targetValue };
      if (nextButtonObj.targetType === 'manual') {
        const { nextNodeId, isSseTrigger } = resolveSseTrigger();
        if (nextNodeId) return { ...fallback, nextNodeId, isSseTrigger };
      }
    }
    return fallback;
  }

  setModalTitle(title: string) {
    this.modalTitle = title;
  }

  loadPreviousNode(): void {
    const prevBtn = this.currentNode?.previous?.find(
      (p: any) => p.buttonId === 'previous_button',
    );
    if (prevBtn?.targetNodeId) this.previousNodeId = prevBtn.targetNodeId;
  }

  clickedOnPrevious() {
    const currentNode = this.workflowData.nodes.find(
      (n: any) => n._id === this.currentNodeId,
    );
    if (currentNode?.previous?.length > 0) {
      const { targetNodeId } = currentNode.previous[0];
      if (targetNodeId) {
        this.workflowData.currentNodeId = targetNodeId;
        this.loadNode(targetNodeId);
      }
    }
  }

  checkPollingServiceForError(value: any) {
    const attemptNode = this.currentNode?.next?.find((next: any) =>
      next.condition.some((condGroup: any) =>
        condGroup.conditions.some((cond: any) => cond.type === 'attempts'),
      ),
    );
    if (attemptNode) {
      const targetNodeId = attemptNode?.condition?.[0]?.targetValue;
      const targetType = attemptNode?.condition?.[0]?.targetType;
      if (targetType === 'node' && targetNodeId) this.loadNode(targetNodeId);
      else if (targetType === 'workflow' && targetNodeId)
        this.loadWorkflow(targetNodeId);
    }
  }

  trackById(index: number, item: any) {
    return item._id;
  }

  reloadData() {
    this.getData();
  }

  @HostListener('document:keydown.escape')
  onEscapeKey() {
    if (this.fields?.fieldName) {
      this.closeModal('connection_modal_' + this.fields.fieldName);
      this.closeModal('coming_soon_modal_' + this.fields.fieldName);
    }
  }

  getIconUrl(option: any): string {
    if (this.integrationIconService.hasLocalMapping(option)) {
      const localFallback =
        this.integrationIconService.getFallbackIconUrl(option);
      if (localFallback) {
        return localFallback;
      }
    }
    const url = option?.iconUrl;
    if (url) {
      const lowerUrl = url.toLowerCase();
      if (
        lowerUrl.includes('solarwindsfileintegritymonitor.png') ||
        lowerUrl.includes('solarwindspatchmanager.png') ||
        lowerUrl.includes('solarwindsnetflowtrafficanalyzer.png') ||
        lowerUrl.includes('solarwindsnetworkperformancemonitor.png')
      ) {
        return '/assets/media/integration/solarwinds.png';
      }
      return url;
    }
    const localFallback =
      this.integrationIconService.getFallbackIconUrl(option);
    if (localFallback) {
      return localFallback;
    }
    return this.defaultIconUrl;
  }

  handleImageError(_option: any, event: Event): void {
    const image = event.target as HTMLImageElement;
    if (!image) {
      return;
    }

    const currentStep = image.dataset['fallbackStep'] || 'original';

    if (currentStep === 'original') {
      image.dataset['fallbackStep'] = 'local';
      const localFallback =
        this.integrationIconService.getFallbackIconUrl(_option);
      if (
        localFallback &&
        localFallback !== this.defaultIconUrl &&
        !image.src.endsWith(localFallback)
      ) {
        image.classList.remove('icm-logo--fallback');
        image.src = localFallback;
        return;
      }
    }

    const domain = this.getDomainForIntegration(_option);

    if (
      (image.dataset['fallbackStep'] === 'local' ||
        currentStep === 'original') &&
      domain
    ) {
      image.dataset['fallbackStep'] = 'google';
      image.classList.remove('icm-logo--fallback');
      image.src = `https://www.google.com/s2/favicons?domain=${domain}&sz=128&default=404`;

      image.onload = () => {
        if (image.dataset['fallbackStep'] === 'google') {
          if (image.naturalWidth < 16 && image.naturalHeight < 16) {
            this.tryDuckDuckGoOrFallback(image, domain);
          }
        }
      };

      image.onerror = () => {
        if (image.dataset['fallbackStep'] === 'google') {
          this.tryDuckDuckGoOrFallback(image, domain);
        }
      };
      return;
    }

    this.setDefaultIcon(image);
  }

  tryDuckDuckGoOrFallback(image: HTMLImageElement, domain: string) {
    image.dataset['fallbackStep'] = 'duckduckgo';
    image.classList.remove('icm-logo--fallback');
    image.onload = () => {
      if (image.naturalWidth < 16 && image.naturalHeight < 16) {
        this.setDefaultIcon(image);
      }
    };
    image.onerror = () => {
      this.setDefaultIcon(image);
    };
    image.src = `https://icons.duckduckgo.com/ip3/${domain}.ico`;
  }

  setDefaultIcon(image: HTMLImageElement) {
    image.onload = null;
    image.onerror = null;
    image.dataset['fallbackStep'] = 'default';
    image.classList.add('icm-logo--fallback');
    image.src = this.defaultIconUrl;
  }

  getDomainForIntegration(option: any): string {
    const url = option?.iconUrl;
    if (
      url &&
      /^https?:\/\//i.test(url) &&
      !url.includes('blob.core.windows.net') &&
      !url.includes('localhost')
    ) {
      try {
        return new URL(url).hostname.replace(/^www\./, '');
      } catch (e) {}
    }
    const name = option?.platformName || option?.name || '';
    if (!name) return '';
    let cleanName = name.toLowerCase().trim();

    if (cleanName.includes('godaddy')) {
      return 'godaddy.com';
    }
    if (cleanName.includes('cloudflare')) {
      return 'cloudflare.com';
    }
    if (cleanName.includes('juniper')) {
      return 'juniper.net';
    }
    if (cleanName.includes('forcepoint')) {
      return 'forcepoint.com';
    }
    if (cleanName.includes('akamai')) {
      return 'akamai.com';
    }
    if (cleanName.includes('f5')) {
      return 'f5.com';
    }
    if (cleanName.includes('barracuda')) {
      return 'barracuda.com';
    }
    if (cleanName.includes('pfsense')) {
      return 'pfsense.org';
    }
    if (cleanName.includes('plixer')) {
      return 'plixer.com';
    }
    if (cleanName.includes('aruba')) {
      return 'arubanetworks.com';
    }
    if (cleanName.includes('airdefense')) {
      return 'extremenetworks.com';
    }
    if (cleanName.includes('aqua security') || cleanName.includes('aquasec')) {
      return 'aquasec.com';
    }
    if (cleanName.includes('quick heal') || cleanName.includes('quickheal')) {
      return 'quickheal.com';
    }
    if (cleanName.includes('kaspersky')) {
      return 'kaspersky.com';
    }
    if (cleanName.includes('cisco')) {
      return 'cisco.com';
    }
    if (cleanName.includes('palo alto') || cleanName.includes('paloalto')) {
      return 'paloaltonetworks.com';
    }
    if (cleanName.includes('sumo logic') || cleanName.includes('sumologic')) {
      return 'sumologic.com';
    }
    if (
      cleanName.includes('fortisoar') ||
      cleanName.includes('fortinet') ||
      cleanName.includes('forti')
    ) {
      return 'fortinet.com';
    }
    if (cleanName.includes('zscaler') || cleanName.includes('zscalar')) {
      return 'zscaler.com';
    }
    if (
      cleanName.includes('digital guardian') ||
      cleanName.includes('digitalguardian')
    ) {
      return 'digitalguardian.com';
    }
    if (
      cleanName.includes('recorded future') ||
      cleanName.includes('recordedfuture')
    ) {
      return 'recordedfuture.com';
    }

    cleanName = cleanName
      .replace(/workspace/g, '')
      .replace(/active directory/g, '')
      .replace(/entra id/g, '')
      .replace(/software/g, '')
      .replace(/platform/g, '')
      .replace(/email configuration/g, '')
      .replace(/llc/g, '')
      .trim();
    const firstWord = cleanName.split(/[\s\-\_]+/)[0];
    if (!firstWord) return '';
    return `${firstWord}.com`;
  }
}
